import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Skeleton } from '@mlm/ui';
import {
  changeReferralCode,
  getReferralCode,
  getSlotShareLinks,
  type SlotShareLink,
} from '../../lib/api';

const ERRORS: Record<string, string> = {
  code_taken: 'That code is already taken or reserved. Pick another.',
  code_is_slot_code: 'That matches a slot code. Pick a different code.',
  change_limit_reached: "You've reached the maximum number of code changes.",
  invalid_referral_code: 'Use 4–32 letters, numbers, or underscore.',
  referral_self_set_disabled: 'Changing your referral code is not enabled.',
  slot_not_owned: "That slot isn't yours.",
  slot_not_paid: 'Only paid slots can have a custom code.',
};

export function ReferralCodeTab() {
  // Account-level budget + enabled flag.
  const info = useQuery({ queryKey: ['referral-code'], queryFn: getReferralCode });
  // Per-slot codes (paid active slots only).
  const slots = useQuery({ queryKey: ['slot-share-links'], queryFn: getSlotShareLinks });

  if (info.isLoading || slots.isLoading) return <Skeleton className="h-40" />;
  const meta = info.data;
  const links = slots.data ?? [];

  if (!meta?.enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Referral codes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Custom referral codes aren't enabled on this site. Each slot's code is its slot code.
          </p>
          <ul className="space-y-1 text-sm">
            {links.map((s) => (
              <li key={s.slotId}>
                <span className="text-muted-foreground">{s.membershipName}:</span>{' '}
                <span className="font-mono font-semibold">{s.customCode ?? s.slotCode}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    );
  }

  const changesLeft = Math.max(0, meta.maxChanges - meta.changesUsed);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Referral codes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Set a custom referral code per slot. When set, it's used instead of the slot code in your
          share links — anyone who buys/registers through it credits that slot. Once someone signs up
          with a code, it stays reserved to you even if you change it. Changes left: {changesLeft} of{' '}
          {meta.maxChanges}.
        </p>
        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground">You have no active paid slots yet.</p>
        ) : (
          links.map((s) => (
            <SlotCodeRow key={s.slotId} slot={s} canChange={changesLeft > 0} />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function SlotCodeRow({ slot, canChange }: { slot: SlotShareLink; canChange: boolean }) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setCode(slot.customCode ?? '');
  }, [slot.customCode]);

  const mut = useMutation({
    meta: { successMessage: 'Referral code updated.' },
    mutationFn: () => changeReferralCode({ code: code.trim().toUpperCase(), slotId: slot.slotId }),
    onSuccess: () => {
      setError(null);
      setDone(true);
      setTimeout(() => setDone(false), 2400);
      qc.invalidateQueries({ queryKey: ['slot-share-links'] });
      qc.invalidateQueries({ queryKey: ['referral-code'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: unknown) => {
      const token = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(ERRORS[token ?? ''] ?? 'Failed to update referral code.');
    },
  });

  const current = slot.customCode ?? slot.slotCode;
  const unchanged = code.trim().toUpperCase() === (slot.customCode ?? '');

  return (
    <div className="rounded-md border p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium">{slot.membershipName}</span>
        <span className="text-[11px] text-muted-foreground">
          slot code <span className="font-mono">{slot.slotCode}</span>
        </span>
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        In use: <span className="font-mono">{current}</span>
        {!slot.customCode && ' (slot code — no custom code set)'}
      </p>
      <div className="flex items-center gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={slot.slotCode}
          maxLength={32}
          disabled={!canChange}
          className="font-mono"
        />
        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !canChange || unchanged || code.trim().length < 4}
          size="sm"
        >
          {mut.isPending ? 'Saving…' : done ? 'Saved' : 'Save'}
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">4–32 letters, numbers, or underscore.</p>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
